import {useCallback, useEffect, useRef, useState} from 'react';
import {fetchEventSource, FetchEventSourceInit} from '@microsoft/fetch-event-source';
import {getAiAgentServiceURL} from 'src/utils/helper';
import {getAuthToken} from '../utils/getAuthToken';
import {AiAgentChartData, AiAgentPdfExport, AiAgentTableData, AiAgentUsage} from 'src/types';

export interface StreamDoneResult {
    text: string;
    charts: AiAgentChartData[] | null;
    tables: AiAgentTableData[] | null;
    exports: AiAgentPdfExport[] | null;
    truncated: boolean;
    title: string | null;
    usage: AiAgentUsage | null;
    isError?: boolean;
}

// Structured data attached to the just-finished turn, delivered on the `visuals`
// event and re-attached to the current bubble (matches persisted history).
export type StreamVisualsCallback = (
    charts: AiAgentChartData[],
    tables: AiAgentTableData[],
    exports: AiAgentPdfExport[],
) => void;

export interface StreamState {
    isStreaming: boolean;
    toolCallInProgress: string | null;
    isThinking: boolean;
    error: string | null;
}

// One row of a batch approval — a single write the agent intends to perform.
// `description` is the agent's own sentence in the user's language ("Pl. 205 —
// Wednesday 05:00–07:00"); `tool`/`args` are the technical detail behind it.
export interface ApprovalAction {
    index: number;
    tool: string;
    description: string;
    args: Record<string, unknown>;
}

// Emitted by the backend when the agent wants to run write tools that need a
// human approve/deny (create/update/delete). The run is parked until decided.
//
// `kind` distinguishes the two shapes. A bulk request ("a weekly schedule on 4
// stations") is dozens of writes, so the backend groups them into ONE card:
// kind='batch' carries the full `actions` list, which the user reviews as a table
// and approves in a single click. kind='single' is the one-action card (no
// `actions`). Both always carry `summary`, so an unknown/missing kind still
// renders something meaningful.
export interface ApprovalRequest {
    runId: string;
    kind?: 'single' | 'batch';
    tool?: string;
    args?: Record<string, unknown>;
    summary: string;
    toolCallId: string;
    title?: string | null;
    actionCount?: number;
    actions?: ApprovalAction[];
}

export const useStreamChat = () => {
    const [state, setState] = useState<StreamState>({
        isStreaming: false,
        toolCallInProgress: null,
        isThinking: false,
        error: null,
    });
    const [pendingApproval, setPendingApproval] = useState<ApprovalRequest | null>(null);
    // A Stop was requested but the run hasn't wound down yet. The wind-down is
    // cooperative, so a tool call already in flight finishes first — this keeps the
    // button honest ("stopping…") instead of pretending it stopped instantly.
    const [isStopping, setIsStopping] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    // Callbacks of the currently-active stream, so an approval decision can
    // re-attach a live tail to the resumed run (see submitApproval).
    const streamCallbacksRef = useRef<{
        onToken: (s: string) => void;
        onDone: (r: StreamDoneResult) => void;
        onNewSegment?: () => void;
        onVisuals?: StreamVisualsCallback;
    } | null>(null);
    // Tool-call ids whose approval we've already answered — suppress a replayed
    // approval_request for them so the card doesn't flash back on the
    // post-decision reconnect. Keyed by toolCallId (not runId) so a run that
    // gates several write tools still shows a card for each one.
    const decidedToolCallsRef = useRef<Set<string>>(new Set());
    // Highest SSE `id` (sequence number) delivered on the active stream. Lets a
    // post-approval reconnect resume from lastSeq+1 (tail only) instead of
    // from_seq=0 (full replay), which would re-emit every `visuals` already shown
    // and duplicate the tables/charts on the frontend.
    const lastSeqRef = useRef<number>(-1);

    // Shared SSE consumer for both the initial POST /chat/stream and the
    // GET reconnect /sessions/{id}/stream. Both speak the same event contract
    // (token / tool_call / thinking / done / error), so the only difference is
    // the request shape passed in `init`.
    const consumeStream = useCallback(async (
        url: string,
        init: Pick<FetchEventSourceInit, 'method' | 'headers' | 'body'>,
        onToken: (segmentText: string) => void,
        onDone: (result: StreamDoneResult) => void,
        onNewSegment?: () => void,
        onVisuals?: StreamVisualsCallback,
        // When resuming a run mid-answer (post-approval tail), the tool_call event
        // that would normally flag a segment boundary was already consumed on the
        // original stream and is NOT replayed — so the caller seeds the boundary
        // here, ensuring the continuation lands in a fresh bubble rather than
        // overwriting the pre-tool narration.
        initialPendingSegment = false,
    ) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setState({isStreaming: true, toolCallInProgress: null, isThinking: false, error: null});
        // NOTE: attaching a reader must NOT clear `pendingApproval`. The post-decision
        // re-attach in submitApproval races the agent: with several writes in one turn
        // the NEXT approval_request arrives on the still-open reader within
        // milliseconds (no LLM round-trip between them), so the card is already on
        // screen by the time we re-attach. Clearing here wiped it, and the re-attach
        // resumes from lastSeq+1 — past that event — so it was never replayed either:
        // the second card stayed invisible until a page reload. Clearing is now the
        // caller's job, done only where a fresh card is guaranteed to follow.
        streamCallbacksRef.current = {onToken, onDone, onNewSegment, onVisuals};

        // The agent answers across several turns (interim narration → tool calls →
        // more text). The backend persists each turn's text as its OWN assistant
        // message, so to keep the LIVE view identical to reloaded history we split
        // the stream into separate bubbles at each text→tool-call boundary rather
        // than concatenating everything into one. `segment` is the CURRENT bubble;
        // `accumulatedText` is a fallback for the non-streamed edge case.
        let segment = '';
        let accumulatedText = '';
        let pendingNewSegment = initialPendingSegment;
        let handledInOpen = false;

        // Guard: drop late callbacks if this stream was aborted (session switch, unmount, etc.)
        const isCancelled = () => controller.signal.aborted;

        await fetchEventSource(url, {
            ...init,
            signal: controller.signal,

            async onopen(response) {
                if (isCancelled()) return;
                const contentType = response.headers.get('content-type') || '';
                if (response.ok && contentType.includes('text/event-stream')) {
                    return;
                }
                // Backend rejected before stream opened (403 blocked, 429 rate limit,
                // 409 already-running, 404 no-active-run on reconnect).
                if (response.status === 429 || response.status === 403) {
                    try {
                        const data = await response.json();
                        if (!isCancelled()) {
                            handledInOpen = true;
                            onDone({
                                text: data.detail || 'An error occurred.',
                                charts: null,
                                tables: null,
                                exports: null,
                                truncated: false,
                                title: null,
                                usage: null,
                                isError: true,
                            });
                        }
                    } catch {
                        // fall through to generic error
                    }
                }
                throw new Error(`http_${response.status}`);
            },

            onmessage(event) {
                if (isCancelled()) return;
                // Track the last delivered sequence id so a reconnect can tail
                // from lastSeq+1 rather than replaying the whole buffer.
                if (event.id) {
                    const seq = Number(event.id);
                    if (!Number.isNaN(seq)) lastSeqRef.current = seq;
                }
                try {
                    const parsed = JSON.parse(event.data);

                    switch (event.event) {
                        case 'token':
                            // First text after a tool call belongs to a NEW bubble.
                            if (pendingNewSegment) {
                                pendingNewSegment = false;
                                segment = '';
                                onNewSegment?.();
                            }
                            segment += parsed.token;
                            accumulatedText += parsed.token;
                            setState((prev) => ({...prev, toolCallInProgress: null, isThinking: false}));
                            onToken(segment);
                            break;

                        case 'tool_call':
                            // If the current bubble already has narration, the next text
                            // starts a fresh bubble (matches how the backend persists turns).
                            if (segment.trim()) pendingNewSegment = true;
                            setState((prev) => ({...prev, toolCallInProgress: parsed.tool, isThinking: false}));
                            break;

                        case 'thinking':
                            // Heartbeat from the backend during hidden reasoning — show a
                            // lightweight "thinking…" indicator (no reasoning content is sent).
                            setState((prev) => ({...prev, isThinking: true}));
                            break;

                        case 'visuals':
                            // Charts/tables produced by the turn that just finished — attach
                            // them to the CURRENT bubble so each renders under the narration
                            // that introduced it (not all dumped at the end).
                            onVisuals?.(parsed.charts ?? [], parsed.tables ?? [], parsed.exports ?? []);
                            break;

                        case 'approval_request':
                            // Agent wants to run a write tool — park the UI and show an
                            // approve/deny card. The run stays open on the backend.
                            // If we already answered this run (post-decision reconnect
                            // replays the event), don't flash the card back up.
                            if (decidedToolCallsRef.current.has((parsed as ApprovalRequest).toolCallId)) {
                                setPendingApproval(null);
                                break;
                            }
                            setState((prev) => ({...prev, toolCallInProgress: null, isThinking: false}));
                            setPendingApproval(parsed as ApprovalRequest);
                            break;

                        case 'approval_resolved':
                            setPendingApproval(null);
                            break;

                        case 'done':
                            setState({isStreaming: false, toolCallInProgress: null, isThinking: false, error: null});
                            setPendingApproval(null);
                            // The last bubble already holds `segment`; charts/tables (collected
                            // across the whole run) attach to it, matching persisted history.
                            // Fall back to parsed.response only when nothing streamed at all.
                            if (parsed.response && !accumulatedText) {
                                segment = parsed.response;
                            }
                            onDone({
                                text: segment,
                                charts: parsed.charts ?? null,
                                tables: parsed.tables ?? null,
                                exports: parsed.exports ?? null,
                                truncated: Boolean(parsed.truncated),
                                title: parsed.title ?? null,
                                usage: parsed.usage ?? null,
                            });
                            break;

                        case 'interrupted':
                            // Run was cancelled mid-answer (server shutdown). Finalize
                            // cleanly on whatever streamed so far so the bubble persists
                            // instead of the stream silently hanging.
                            setState({isStreaming: false, toolCallInProgress: null, isThinking: false, error: null});
                            setPendingApproval(null);
                            onDone({
                                text: segment || parsed.detail || 'Response interrupted.',
                                charts: null,
                                tables: null,
                                exports: null,
                                truncated: true,
                                title: null,
                                usage: null,
                            });
                            break;

                        case 'error':
                            // Show backend error message as a regular assistant response
                            setState({isStreaming: false, toolCallInProgress: null, isThinking: false, error: null});
                            setPendingApproval(null);
                            onDone({
                                text: parsed.detail || 'An error occurred.',
                                charts: null,
                                tables: null,
                                exports: null,
                                truncated: false,
                                title: null,
                                usage: null,
                                isError: true,
                            });
                            break;
                    }
                } catch {
                    // skip malformed JSON
                }
            },

            onerror(err) {
                if (isCancelled()) {
                    throw err; // stop retrying silently
                }
                if (handledInOpen) {
                    setState({isStreaming: false, toolCallInProgress: null, isThinking: false, error: null});
                    throw err; // stop retrying, but don't show generic error
                }
                setState({
                    isStreaming: false,
                    toolCallInProgress: null,
                    isThinking: false,
                    error: err instanceof Error ? err.message : 'Connection lost',
                });
                throw err; // stop retrying
            },

            openWhenHidden: true,
        });
    }, []);

    const sendMessage = useCallback(async (
        sessionId: string,
        message: string,
        activeBusinessId: number | undefined,
        onToken: (segmentText: string) => void,
        onDone: (result: StreamDoneResult) => void,
        onNewSegment?: () => void,
        onVisuals?: StreamVisualsCallback,
    ) => {
        const token = getAuthToken();
        // New turn → prior per-tool approval decisions are irrelevant; clear so the
        // set can't grow unbounded across a long conversation. Any card still on
        // screen belongs to the finished turn, so it goes too.
        decidedToolCallsRef.current.clear();
        setPendingApproval(null);
        await consumeStream(
            `${getAiAgentServiceURL()}/chat/stream`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? {Authorization: token} : {}),
                },
                body: JSON.stringify({session_id: sessionId, message, active_business_id: activeBusinessId}),
            },
            onToken,
            onDone,
            onNewSegment,
            onVisuals,
        );
    }, [consumeStream]);

    // Ask the backend whether a run is still executing for this session. Called
    // on session open/reload to decide between reconnecting to a live stream and
    // just showing finished history.
    const checkActiveRun = useCallback(async (sessionId: string): Promise<boolean> => {
        const token = getAuthToken();
        try {
            const res = await fetch(
                `${getAiAgentServiceURL()}/sessions/${sessionId}/active-run`,
                {headers: {...(token ? {Authorization: token} : {})}},
            );
            if (!res.ok) return false;
            const data = await res.json();
            return Boolean(data.active);
        } catch {
            return false;
        }
    }, []);

    // Reconnect to an in-flight run: replay everything buffered so far (from_seq=0)
    // then tail live. The agent kept working while we were gone — this picks the
    // stream back up at its current point.
    const streamReconnect = useCallback(async (
        sessionId: string,
        onToken: (segmentText: string) => void,
        onDone: (result: StreamDoneResult) => void,
        onNewSegment?: () => void,
        onVisuals?: StreamVisualsCallback,
    ) => {
        const token = getAuthToken();
        // Safe to clear: a full replay (from_seq=0) re-emits the approval_request if
        // the run is still parked, so a live card is rebuilt rather than lost.
        setPendingApproval(null);
        await consumeStream(
            `${getAiAgentServiceURL()}/sessions/${sessionId}/stream?from_seq=0`,
            {
                method: 'GET',
                headers: {...(token ? {Authorization: token} : {})},
            },
            onToken,
            onDone,
            onNewSegment,
            onVisuals,
        );
    }, [consumeStream]);

    // Deliver a human approve/deny decision for a parked write tool. The backend
    // un-parks the run and the SAME open stream resumes (executes or adapts).
    const submitApproval = useCallback(async (
        sessionId: string,
        runId: string,
        toolCallId: string,
        approved: boolean,
        reason?: string,
    ) => {
        const token = getAuthToken();
        decidedToolCallsRef.current.add(toolCallId);
        setPendingApproval(null); // optimistic — the approval_resolved event confirms
        try {
            await fetch(`${getAiAgentServiceURL()}/sessions/${sessionId}/approval`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', ...(token ? {Authorization: token} : {})},
                body: JSON.stringify({run_id: runId, approved, reason: reason ?? null}),
            });
        } catch {
            return; // couldn't deliver the decision — nothing to tail
        }
        // Re-attach a live tail to the RESUMED run. The stream that showed the
        // approval card may be a reconnect that stopped delivering after a long
        // park. Resume from lastSeq+1 (tail only) — NOT from_seq=0: the buffer
        // already replayed everything up to the approval_request, so a full replay
        // would re-emit every `visuals` and duplicate the tables/charts already on
        // screen. Tailing delivers only approval_resolved + the continuation.
        const cbs = streamCallbacksRef.current;
        if (cbs) {
            const fromSeq = lastSeqRef.current >= 0 ? lastSeqRef.current + 1 : 0;
            // Awaited but caught: this reader lives until the run ends OR until the
            // NEXT decision supersedes it (consumeStream aborts the previous one), and
            // an aborted reader rejects. Callers fire submitApproval and forget, so
            // without this every approval after the first raised an unhandled
            // rejection when the following one replaced its stream.
            await consumeStream(
                `${getAiAgentServiceURL()}/sessions/${sessionId}/stream?from_seq=${fromSeq}`,
                {method: 'GET', headers: {...(token ? {Authorization: token} : {})}},
                cbs.onToken,
                cbs.onDone,
                cbs.onNewSegment,
                cbs.onVisuals,
                true, // continuation is a new turn — seed the segment boundary
            ).catch(() => undefined);
        }
    }, [consumeStream]);

    const clearError = useCallback(() => {
        setState((prev) => ({...prev, error: null}));
    }, []);

    // Ask the backend to stop the in-flight run. We deliberately do NOT abort the SSE
    // reader here: the backend winds the run down gracefully and sends a normal
    // terminal `done` (with the partial answer kept), which the open stream turns into
    // a finished message the usual way. Killing the reader instead would leave the
    // message half-rendered and the run finishing invisibly in the background.
    const stopRun = useCallback(async (sessionId: string) => {
        const token = getAuthToken();
        setIsStopping(true);
        try {
            await fetch(`${getAiAgentServiceURL()}/sessions/${sessionId}/stop`, {
                method: 'POST',
                headers: {...(token ? {Authorization: token} : {})},
            });
        } catch {
            // Couldn't deliver the request (offline / server gone). Re-enable the button
            // so the user can retry; the run either keeps going or is already dead.
            setIsStopping(false);
        }
    }, []);

    const abortStream = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        decidedToolCallsRef.current.clear();  // session switch — drop stale decisions
        setState({isStreaming: false, toolCallInProgress: null, isThinking: false, error: null});
        setPendingApproval(null);
    }, []);

    // Every way a stream can end (done / interrupted / error / abort) clears
    // isStreaming, so hang the "stopping…" reset off that one signal rather than
    // repeating it in each exit path.
    useEffect(() => {
        if (!state.isStreaming) setIsStopping(false);
    }, [state.isStreaming]);

    // Cleanup on unmount: abort any in-flight SSE reader. The backend run keeps
    // going and can be reconnected to on the next mount.
    useEffect(() => () => {
        abortRef.current?.abort();
    }, []);

    return {
        ...state,
        pendingApproval,
        isStopping,
        sendMessage,
        checkActiveRun,
        streamReconnect,
        submitApproval,
        stopRun,
        clearError,
        abortStream,
    };
};
